import * as React from 'react'

import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  AllowanceStatFragment,
  NewStatsCounterAllowanceStatsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import CrashLogo from 'modules/core/components/CrashLogo'
import Loading from 'modules/core/components/Loading'
import { i18nDateToString } from 'modules/core/lib/dates'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import CarryOverExplainer from 'modules/timeoff/components/Leaves/CarryOverExplainer'
import styles from 'modules/timeoff/components/Leaves/Overview/index.module.css'
import type Allowance from 'modules/timeoff/store/models/Allowance'

import AvailableDays from './Available'
import Counter from './Counter'
import { transformAllowanceStat } from './allowanceStatUtils'

const getAllowanceTypeLabel = (type: string) => {
  switch (type) {
    case 'days':
      return i18n.t('time_off.allowance_type.days')
    case 'hours':
      return i18n.t('time_off.allowance_type.hours')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface NameProps {
  allowance: Allowance
}

const Name = ({ allowance }: NameProps) => {
  return (
    <Box overflowX="hidden">
      <Text.Normal size="200" color="grey1000" weight="medium" ellipsis>
        {allowance.attributes.name}
      </Text.Normal>
    </Box>
  )
}

interface Props {
  allowance: Allowance
  employeeId: string
}

const NewStatsCounter = ({ allowance, employeeId }: Props) => {
  const today = React.useMemo(() => moment(), [])
  const referenceDate = i18nDateToString(today, 'backend')

  const { data, loading, error } = useQuery(NewStatsCounterAllowanceStatsDocument, {
    variables: {
      employeeId,
      allowanceIds: [allowance.attributes.id],
      referenceDate,
    },
  })

  if (loading) return <Loading />

  // Handle GraphQL errors separately from empty data
  if (error) {
    return <CrashLogo />
  }

  const allowanceStatNode: AllowanceStatFragment | undefined =
    data?.employees?.employee?.allowanceStatsConnection?.nodes?.[0]

  // No stats available for this allowance - this can happen when there is no
  // allowance stat configured for the selected reference date.
  if (!allowanceStatNode) return null

  const currentStat = transformAllowanceStat(allowanceStatNode)
  const currentCycle = currentStat.cycleByDate(today)

  if (!currentCycle) return <CrashLogo />

  const nextCycle = currentStat.nextCycle(currentCycle)

  return (
    <Box
      flexDirection="column"
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs008',
      }}
      padding="s2"
      background="grey200"
      width="full"
      justifyContent="spaceBetween"
    >
      <Box
        border={{
          bottom: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        justifyContent="center"
      >
        <div className={styles.container}>
          <div className={styles.icon}>
            <CarryOverExplainer
              accruedByTimeWorked={currentStat.attributes.prorated_allowance_days}
              allowance={allowance}
              cycle={currentCycle}
            />
          </div>
          <div className={styles.title}>
            <Box
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              justifyContent="center"
              paddingY="s8"
            >
              <Name allowance={allowance} />
            </Box>
          </div>
          <div className={styles.tag}>
            <Tag type="neutral">
              {getAllowanceTypeLabel(allowance.attributes.allowance_type ?? '')}
            </Tag>
          </div>
        </div>
      </Box>
      <Box
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        width="full"
        gap="s8"
        paddingY="s16"
      >
        <AvailableDays
          allowance={allowance}
          currentStat={currentStat}
          currentCycle={currentCycle}
          nextCycle={nextCycle}
        />
        <Counter
          employeeId={employeeId}
          allowance={allowance}
          currentStat={currentStat}
          currentCycle={currentCycle}
        />
      </Box>
    </Box>
  )
}

export default NewStatsCounter
